import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  MenuItem,
  Select,
  FormControl,
  Chip,
  TextField,
  Button,
  Avatar,
  Typography,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import api from "../../api";

export const PURPLE = "#7c3aed";
export const PURPLE_DARK = "#6d28d9";
export const PURPLE_SOFT = "#f5f3ff";

export const HOLIDAY_TYPES = {
  christmas:  { label: "Christmas",  color: "#dc2626", bg: "#fee2e2", border: "#fca5a5", icon: "🎄" },
  easter:     { label: "Easter",     color: "#7c3aed", bg: "#ede9fe", border: "#c4b5fd", icon: "🐣" },
  "half-term":{ label: "Half-term",  color: "#2563eb", bg: "#dbeafe", border: "#93c5fd", icon: "📅" },
  summer:     { label: "Summer",     color: "#d97706", bg: "#fef3c7", border: "#fcd34d", icon: "☀️" },
  custom:     { label: "Holiday",    color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", icon: "🏖️" },
};

export function detectHolidayType(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("christmas") || n.includes("xmas") || n.includes("new year")) return "christmas";
  if (n.includes("easter")) return "easter";
  if (n.includes("summer")) return "summer";
  if (n.includes("half") || n.includes("halfterm")) return "half-term";
  return "custom";
}

export const HOLIDAY_NAME_PRESETS = [
  { name: "UK Christmas",       type: "christmas"   },
  { name: "Easter Holiday",     type: "easter"      },
  { name: "February Half-term", type: "half-term"   },
  { name: "May Half-term",      type: "half-term"   },
  { name: "October Half-term",  type: "half-term"   },
  { name: "Summer Holiday",     type: "summer"      },
];

export const courseNameOf = (c) =>
  c?.course_name || c?.name?.replace(/\s*[-–]\s*\d{4}\/\d{4}\s*$/, "") || c?.name || "";

export const UK_HOLIDAY_PRESETS_DATES = [
  { type: "christmas",  name: "UK Christmas",        monthStart: 12, dayStart: 19, monthEnd: 1,  dayEnd: 8  },
  { type: "half-term",  name: "February Half-term",  monthStart: 2,  dayStart: 10, monthEnd: 2,  dayEnd: 21 },
  { type: "easter",     name: "Easter Holiday",      monthStart: 3,  dayStart: 24, monthEnd: 4,  dayEnd: 20 },
  { type: "half-term",  name: "May Half-term",       monthStart: 5,  dayStart: 23, monthEnd: 6,  dayEnd: 2  },
  { type: "summer",     name: "Summer Holiday",      monthStart: 7,  dayStart: 17, monthEnd: 9,  dayEnd: 1  },
  { type: "half-term",  name: "October Half-term",   monthStart: 10, dayStart: 20, monthEnd: 10, dayEnd: 31 },
];

function monthDayInRange(month, day, monthStart, dayStart, monthEnd, dayEnd) {
  const val = month * 100 + day;
  const start = monthStart * 100 + dayStart;
  const end = monthEnd * 100 + dayEnd;
  if (start <= end) return val >= start && val <= end;
  return val >= start || val <= end;
}

export function autoDetectUKHolidays(weeks) {
  const result = {};
  weeks.forEach((w) => {
    const s = new Date(w.start_date);
    const e = new Date(w.end_date);
    for (const preset of UK_HOLIDAY_PRESETS_DATES) {
      const sM = s.getMonth() + 1, sD = s.getDate();
      const eM = e.getMonth() + 1, eD = e.getDate();
      if (
        monthDayInRange(sM, sD, preset.monthStart, preset.dayStart, preset.monthEnd, preset.dayEnd) ||
        monthDayInRange(eM, eD, preset.monthStart, preset.dayStart, preset.monthEnd, preset.dayEnd)
      ) {
        result[w.id] = { is_holiday: true, holiday_name: preset.name };
        break;
      }
    }
  });
  return result;
}

export function StepBadge({ n }) {
  return (
    <Avatar sx={{ width: 28, height: 28, fontSize: 14, fontWeight: 700, bgcolor: PURPLE, color: "#fff" }}>
      {n}
    </Avatar>
  );
}

function StatChip({ icon, value, label, color = PURPLE, bg = PURPLE_SOFT }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.5, py: 0.5 }}>
      <Avatar sx={{ width: 36, height: 36, bgcolor: bg, color }}>{icon}</Avatar>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1}>{value}</Typography>
        <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>{label}</Typography>
      </Box>
    </Stack>
  );
}

const modeChip = (mode) =>
  mode === "online"
    ? { label: "Online", bg: "#ede9fe", color: PURPLE }
    : { label: "In Person", bg: "#fef3c7", color: "#b45309" };

const statusChipStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return { label: "Active", bg: "#dcfce7", color: "#15803d" };
  if (s === "upcoming") return { label: "Upcoming", bg: "#dbeafe", color: "#1d4ed8" };
  if (s === "completed") return { label: "Completed", bg: "#e2e8f0", color: "#475569" };
  return { label: status || "—", bg: "#e2e8f0", color: "#475569" };
};

export function FilterChip({ label, active, onClick }) {
  return (
    <Chip
      size="small"
      label={label}
      onClick={onClick}
      sx={{
        height: 24,
        fontWeight: 600,
        cursor: "pointer",
        bgcolor: active ? PURPLE : "#f1f5f9",
        color: active ? "#fff" : "#64748b",
        "&:hover": { bgcolor: active ? PURPLE_DARK : "#e2e8f0" },
      }}
    />
  );
}

export function CourseHeader({
  academicCourses,
  acdemicCourseId,
  setAcdemicCourseId,
  plans,
  worksheetCount,
  worksheetLabel,
  viewCourseHref,
}) {
  const modesCount = new Set(plans.map((p) => p.mode)).size;
  const locationsCount = new Set(plans.map((p) => p.location || "—")).size;

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: "1px solid #eef2f7" }}>
      <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
        <StepBadge n={1} />
        <Typography variant="subtitle1" fontWeight={700}>Select Course</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ flex: "0 0 auto", width: { xs: "100%", md: 320 } }}>
          <Select
            value={acdemicCourseId}
            onChange={(e) => setAcdemicCourseId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>Select Course</MenuItem>
            {academicCourses.map((c) => (
              <MenuItem key={c.id} value={c.id}>{courseNameOf(c) || `Course #${c.id}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {viewCourseHref && (
          <Button
            variant="outlined"
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
            component={RouterLink}
            to={viewCourseHref}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#e5e7eb",
              color: "text.primary",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: PURPLE, color: PURPLE, bgcolor: PURPLE_SOFT },
            }}
          >
            View Course
          </Button>
        )}
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
        <StatChip icon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />} value={worksheetCount} label={worksheetLabel} />
        <StatChip icon={<GroupsOutlinedIcon sx={{ fontSize: 20 }} />} value={plans.length} label="Groups" color="#16a34a" bg="#dcfce7" />
        <StatChip
          icon={<LayersOutlinedIcon sx={{ fontSize: 20 }} />}
          value="Groups"
          label={`${modesCount} Mode · ${locationsCount} Location`}
          color="#d97706"
          bg="#fef3c7"
        />
      </Stack>
    </Paper>
  );
}

export function GroupsSidebar({ plans, planId, setPlanId, academicCourseLabel }) {
  const [groupSearch, setGroupSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const visiblePlans = useMemo(
    () =>
      plans.filter((p) => {
        const name = (p.plan_name || p.name || "").toLowerCase();
        if (groupSearch && !name.includes(groupSearch.toLowerCase())) return false;
        if (modeFilter === "online" && p.mode !== "online") return false;
        if (modeFilter === "in-person" && p.mode === "online") return false;
        if (statusFilter !== "all" && String(p.status || "").toLowerCase() !== statusFilter) return false;
        return true;
      }),
    [plans, groupSearch, modeFilter, statusFilter]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid #eef2f7",
        bgcolor: "#fff",
        position: { md: "sticky" },
        top: { md: 16 },
        maxHeight: { md: "calc(100vh - 32px)" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
        <StepBadge n={2} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {academicCourseLabel ? `Groups for ${academicCourseLabel}` : "Groups"}
        </Typography>
        {plans.length > 0 && (
          <Chip
            size="small"
            label={`${plans.length} total`}
            sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600, height: 22 }}
          />
        )}
      </Stack>

      {plans.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <GroupsOutlinedIcon sx={{ fontSize: 36, color: "#e2e8f0", mb: 1 }} />
          <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }}>
            {academicCourseLabel ? "No groups for this course" : "Select a course to see groups"}
          </Typography>
        </Box>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder="Search groups…"
            value={groupSearch}
            onChange={(e) => setGroupSearch(e.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#374151" }} />
                </InputAdornment>
              ),
            }}
          />

          <Stack spacing={0.75} mb={1}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" sx={{ color: "#374151", fontWeight: 600, width: "100%", mb: 0.25 }}>
                Mode
              </Typography>
              <FilterChip label="All" active={modeFilter === "all"} onClick={() => setModeFilter("all")} />
              <FilterChip
                label="Online"
                active={modeFilter === "online"}
                onClick={() => setModeFilter(modeFilter === "online" ? "all" : "online")}
              />
              <FilterChip
                label="In Person"
                active={modeFilter === "in-person"}
                onClick={() => setModeFilter(modeFilter === "in-person" ? "all" : "in-person")}
              />
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" sx={{ color: "#374151", fontWeight: 600, width: "100%", mb: 0.25 }}>
                Status
              </Typography>
              <FilterChip label="All" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
              <FilterChip
                label="Active"
                active={statusFilter === "active"}
                onClick={() => setStatusFilter(statusFilter === "active" ? "all" : "active")}
              />
              <FilterChip
                label="Upcoming"
                active={statusFilter === "upcoming"}
                onClick={() => setStatusFilter(statusFilter === "upcoming" ? "all" : "upcoming")}
              />
              <FilterChip
                label="Completed"
                active={statusFilter === "completed"}
                onClick={() => setStatusFilter(statusFilter === "completed" ? "all" : "completed")}
              />
            </Stack>
          </Stack>

          <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
            {visiblePlans.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }}>
                  No groups match filters
                </Typography>
              </Box>
            ) : (
              <Stack spacing={0.75}>
                {visiblePlans.map((p) => {
                  const selected = String(planId) === String(p.id);
                  const m = modeChip(p.mode);
                  const s = statusChipStyle(p.status);
                  return (
                    <Box
                      key={p.id}
                      onClick={() => setPlanId(p.id)}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: selected ? PURPLE : "#e2e8f0",
                        bgcolor: selected ? PURPLE_SOFT : "#fff",
                        cursor: "pointer",
                        transition: "all 100ms ease",
                        "&:hover": {
                          borderColor: selected ? PURPLE : "#c7d2fe",
                          bgcolor: selected ? PURPLE_SOFT : "#fafafa",
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        {selected ? (
                          <CheckCircleIcon sx={{ color: PURPLE, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ color: "#cbd5e1", fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} mb={0.25}>
                            <Avatar
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: p.mode === "online" ? "#ede9fe" : "#fef3c7",
                                color: p.mode === "online" ? PURPLE : "#b45309",
                              }}
                            >
                              {p.mode === "online" ? (
                                <LanguageIcon sx={{ fontSize: 12 }} />
                              ) : (
                                <ApartmentIcon sx={{ fontSize: 12 }} />
                              )}
                            </Avatar>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                              {p.plan_name || p.name}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} display="block" noWrap>
                            {p.location || "—"} · {p.start_date} – {p.end_date}
                          </Typography>
                          <Stack direction="row" spacing={0.5} mt={0.5} alignItems="center">
                            <Chip
                              size="small"
                              label={m.label}
                              sx={{ bgcolor: m.bg, color: m.color, fontWeight: 600, height: 20, fontSize: 11 }}
                            />
                            <Chip
                              size="small"
                              label={s.label}
                              sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, height: 20, fontSize: 11 }}
                            />
                            {p.available_seats != null && (
                              <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
                                {p.available_seats} seats
                              </Typography>
                            )}
                          </Stack>
                          {(p.weeks || []).length > 0 && (
                            <Box sx={{ mt: 0.75, height: 5, borderRadius: 1, overflow: "hidden", display: "flex", gap: "1px" }}>
                              {(p.weeks || []).map((w) => {
                                const ht = w.is_holiday ? HOLIDAY_TYPES[detectHolidayType(w.holiday_name)] : null;
                                return (
                                  <Tooltip
                                    key={w.id}
                                    title={w.is_holiday ? `W${w.week_number}: ${w.holiday_name || "Holiday"}` : `W${w.week_number}: Teaching`}
                                    arrow
                                  >
                                    <Box sx={{ flex: 1, bgcolor: ht ? ht.bg : "#f0fdf4", borderTop: `2px solid ${ht ? ht.color : "#16a34a"}` }} />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}

export function useAcademicCoursePlans({ loadTests = false } = {}) {
  const [academicCourses, setAcademicCourses] = useState([]);
  const [acdemicCourseId, setAcdemicCourseId] = useState("");
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [tests, setTests] = useState([]);

  useEffect(() => {
    api
      .get("admin/ca_records", { params: { active_only: 1 } })
      .then((r) => setAcademicCourses(r.data?.data || []))
      .catch(() => setAcademicCourses([]));
  }, []);

  useEffect(() => {
    if (!acdemicCourseId) {
      setPlans([]);
      setPlanId("");
      if (loadTests) setTests([]);
      return;
    }
    const selected = academicCourses.find((c) => String(c.id) === String(acdemicCourseId));
    const courseId = selected?.course_id || selected?.course?.id;

    const loadPlans = async () => {
      try {
        const r = await api.get(`admin/acdemic-courses/${acdemicCourseId}/course-plans-with-weeks`);
        let list = r?.data?.plans ?? r?.data?.data?.plans ?? [];
        if (!list.length && courseId) {
          const r2 = await api.get(`admin/courses/${courseId}/course-plans-with-weeks`);
          list = r2?.data?.plans ?? r2?.data?.data?.plans ?? [];
        }
        setPlans(list);
      } catch {
        setPlans([]);
      }
    };
    loadPlans();

    if (loadTests) {
      api
        .get("admin/assign/test", { params: { academic_course_id: acdemicCourseId } })
        .then((r) => {
          const list = r.data?.data?.data ?? r.data?.data ?? [];
          setTests(Array.isArray(list) ? list : []);
        })
        .catch(() => setTests([]));
    }
  }, [acdemicCourseId, academicCourses, loadTests]);

  const selectedPlan = useMemo(
    () => plans.find((p) => String(p.id) === String(planId)) || null,
    [plans, planId]
  );
  const weeks = useMemo(
    () => selectedPlan?.plan_weeks || selectedPlan?.weeks || [],
    [selectedPlan]
  );
  const catalogCourseId = useMemo(() => {
    const selected = academicCourses.find((c) => String(c.id) === String(acdemicCourseId));
    return selected?.course_id || selected?.course?.id || null;
  }, [academicCourses, acdemicCourseId]);
  const selectedAcademic = academicCourses.find((c) => String(c.id) === String(acdemicCourseId));
  const academicCourseLabel = selectedAcademic ? courseNameOf(selectedAcademic) : "";
  const viewCourseHref = catalogCourseId ? `/admin/course/${catalogCourseId}` : null;

  const handleCourseChange = (v) => {
    setAcdemicCourseId(v);
    setPlanId("");
  };

  return {
    academicCourses,
    acdemicCourseId,
    setAcdemicCourseId: handleCourseChange,
    plans,
    planId,
    setPlanId,
    tests,
    selectedPlan,
    weeks,
    catalogCourseId,
    academicCourseLabel,
    viewCourseHref,
  };
}
